/**
 * HomePage — upload + result view, with mode tabs, options panel, and recent conversions.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropZone } from "../components/upload/DropZone";
import { UploadProgress } from "../components/upload/UploadProgress";
import { MarkdownViewer } from "../components/results/MarkdownViewer";
import { DownloadButton } from "../components/results/DownloadButton";
import { ErrorBanner } from "../components/shared/ErrorBanner";
import { LanguageSwitcher } from "../components/shared/LanguageSwitcher";
import { ThemeToggle } from "../components/shared/ThemeToggle";
import { ChangelogButton } from "../components/shared/ChangelogButton";
import { OptionsPanel } from "../components/home/OptionsPanel";
import { RecentConversions } from "../components/home/RecentConversions";
import { MarkdownPreviewTool } from "../components/home/MarkdownPreviewTool";
import { useFileUpload } from "../hooks/useFileUpload";
import { useJobPoller } from "../hooks/useJobPoller";
import { useConversionStore } from "../store/conversionStore";
import { useClipboard } from "../hooks/useClipboard";
import { useRecentConversions, type RecentItem } from "../hooks/useRecentConversions";
import { openMarkdownInReview } from "./ReviewPage";
import type { ConversionResult } from "../api/types";

type Mode = "convert" | "preview";

export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("convert");
  const { uploadState, uploadProgress, result, error } = useConversionStore();
  const { upload, reset } = useFileUpload();
  const { copy, copied } = useClipboard();
  const { items: recentItems, add: addRecent, remove: removeRecent } = useRecentConversions();
  const store = useConversionStore();

  useJobPoller();

  // Save to recent when a new result arrives
  const lastSavedJobId = useRef<string | null>(null);
  useEffect(() => {
    if (result && result.job_id !== lastSavedJobId.current) {
      lastSavedJobId.current = result.job_id;
      addRecent(result);
    }
  }, [result, addRecent]);

  const handleFileSelected = useCallback((file: File) => { upload(file); }, [upload]);

  const handleRestore = useCallback(
    (item: RecentItem) => {
      const restored: ConversionResult = {
        job_id: `recent-${item.timestamp}`,
        filename: item.filename,
        page_count: item.pageCount,
        markdown: item.markdown,
        word_count: item.wordCount,
        images_found: item.imagesFound,
        ocr_pages: [],
        warnings: [],
        duration_ms: item.durationMs,
        created_at: new Date(item.timestamp).toISOString(),
      };
      store.setDone(restored);
    },
    [store]
  );

  const isActive = uploadState === "uploading" || uploadState === "processing";
  const isIdle = uploadState === "idle";

  return (
    <main className="home-page">
      <div className="home-page__topbar">
        <ChangelogButton />
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <header className="home-page__header">
        <h1>{t("title")}</h1>
        <p className="home-page__subtitle">{t("subtitle")}</p>
      </header>

      {/* Mode tabs */}
      <div className="mode-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={mode === "convert"}
          className={`mode-tab${mode === "convert" ? " mode-tab--active" : ""}`}
          onClick={() => setMode("convert")}
        >
          {t("mode.convert")}
        </button>
        <button
          role="tab"
          aria-selected={mode === "preview"}
          className={`mode-tab${mode === "preview" ? " mode-tab--active" : ""}`}
          onClick={() => setMode("preview")}
        >
          {t("mode.preview")}
        </button>
      </div>

      {mode === "preview" && <MarkdownPreviewTool />}

      {mode === "convert" && (
        <>
          {uploadState !== "done" && (
            <section className="home-page__upload">
              <OptionsPanel />
              <DropZone onFileSelected={handleFileSelected} disabled={isActive} />
              <UploadProgress state={uploadState} progress={uploadProgress} />
              {error && (
                <ErrorBanner
                  message={error.message}
                  errorCode={error.error_code}
                  onDismiss={reset}
                />
              )}
            </section>
          )}

          {isIdle && (
            <RecentConversions
              items={recentItems}
              onRestore={handleRestore}
              onRemove={removeRecent}
            />
          )}

          {result && (
            <section className="home-page__result">
              <div className="result-meta">
                <span className="result-meta__item">
                  {t("result.pages_one", { count: result.page_count })}
                </span>
                <span className="result-meta__item">
                  {t("result.words", { count: result.word_count.toLocaleString() })}
                </span>
                {result.images_found > 0 && (
                  <span className="result-meta__item">
                    {t("result.images_one", { count: result.images_found })}
                  </span>
                )}
                <span className="result-meta__item result-meta__item--time">
                  {t("result.duration", { ms: result.duration_ms })}
                </span>
              </div>

              {result.warnings.length > 0 && (
                <div className="result-warnings">
                  {result.warnings.map((w, i) => (
                    <p key={i} className="result-warnings__item">⚠ {w}</p>
                  ))}
                </div>
              )}

              <div className="result-actions">
                <button className="btn btn--primary" onClick={() => copy(result.markdown)}>
                  {copied ? t("actions.copied") : t("actions.copy")}
                </button>
                <DownloadButton markdown={result.markdown} filename={result.filename} />
                <button
                  className="btn btn--secondary"
                  onClick={() => {
                    if (!openMarkdownInReview(result.markdown)) {
                      alert(t("actions.reviewTooLarge"));
                    }
                  }}
                >
                  {t("actions.openReview")}
                </button>
                <button className="btn btn--ghost" onClick={reset}>
                  {t("actions.convertAnother")}
                </button>
              </div>

              <MarkdownViewer markdown={result.markdown} />
            </section>
          )}
        </>
      )}
    </main>
  );
};
